import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, take, skip } from 'rxjs/operators';
import { Subscription, combineLatest } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { environment } from '../../../../environments/environment';

export interface DateRange { gte: string; lte: string; }
export interface NumberRange { gte: number; lte: number; }
export interface FilterQuery {
  query: Array<{
    fish_type?: string[];
    date?: DateRange;
    temperature?: NumberRange;
    wind_speed?: NumberRange;
    wind_direction?: string;
    tide?: string[];
    trip_id?: string;
    conditions?: string[];
  }>;
}

export interface FishType { fish_name: string; fish_full_name: string; fish_display_color: string; icon: string | null; }
export interface WeatherCondition { short_forecast: string; icon: string | null; }
export interface FishingTrip { trip_id: string; start_time: Date; }

export interface FilterEvent {
  query: FilterQuery;
  clientFilters: { notes: string };
}

@Component({
  selector: 'app-catch-filter-panel',
  templateUrl: './catch-filter-panel.component.html',
  styleUrls: ['./catch-filter-panel.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatExpansionModule],
})
export class CatchFilterPanelComponent implements OnInit, OnDestroy {
  @Input() showAnglerNotes = false;
  @Input() showHeatmapToggle = false;
  @Input() heatMap = false;

  @Output() filterChange = new EventEmitter<FilterEvent>();
  @Output() heatmapToggleChange = new EventEmitter<boolean>();

  filterForm: FormGroup;
  
  allFishingTrips: FishingTrip[] = [];
  fishTypes: FishType[] = [];
  tideStates: string[] = [];
  weatherConditions: WeatherCondition[] = [];
  windDirections = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

  private sub!: Subscription;
  private isInitializing = true;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.filterForm = this.fb.group({
      selectedFishTypes: [[]],
      dateRange: this.fb.group({
        start: [''],
        end: ['']
      }),
      temperature: this.fb.group({ min: ['40'], max: ['100'] }),
      windSpeed: this.fb.group({ min: ['0'], max: ['20'] }),
      windDirection: [''],
      tide: [[]],
      trip_id: [''],
      conditions: [[]],
      anglerNotes: ['']
    });
  }

  ngOnInit(): void {
    // 1. Fetch options
    combineLatest([
      this.http.get<FishType[]>(`${environment.apiUrl}/fish_types`),
      this.http.get<WeatherCondition[]>(`${environment.apiUrl}/weather_types`),
      this.http.get<string[]>(`${environment.apiUrl}/tide_types`),
      this.http.post<FishingTrip[]>(`${environment.apiUrl}/fishing_trip`, { action: 'get_all' })
    ]).pipe(take(1)).subscribe(([fishes, weather, tides, trips]) => {
      this.fishTypes = fishes;
      this.weatherConditions = weather;
      this.tideStates = tides;
      this.allFishingTrips = trips;

      // 2. Read URL params OR set defaults
      this.route.queryParams.pipe(take(1)).subscribe(params => {
        const defaultFish = this.fishTypes
          .map(f => f.fish_name)
          .filter(name => name !== 'location' && name !== 'no_fish');

        const defaultStart = new Date(new Date().setFullYear(new Date().getFullYear() - 2)).toISOString().split('T')[0];
        const defaultEnd = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0];

        this.filterForm.patchValue({
          selectedFishTypes: params['fish'] ? params['fish'].split(',') : defaultFish,
          dateRange: {
            start: params['start'] || defaultStart,
            end: params['end'] || defaultEnd
          },
          temperature: {
            min: params['tMin'] || '40',
            max: params['tMax'] || '100'
          },
          windSpeed: {
            min: params['wMin'] || '0',
            max: params['wMax'] || '20'
          },
          windDirection: params['wDir'] || '',
          tide: params['tides'] ? params['tides'].split(',') : [],
          trip_id: params['trip'] || '',
          conditions: params['conds'] ? params['conds'].split(',') : [],
          anglerNotes: params['notes'] || ''
        }, { emitEvent: true }); // intentionally emit to trigger initial data load

        this.isInitializing = false;
      });
    });

    // 3. Listen to form changes
    this.sub = this.filterForm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe((val) => {
      this.updateUrl(val);
      this.emitFilter(val);
    });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  private updateUrl(formValue: any): void {
    if (this.isInitializing) return;

    const params: any = {
      fish: formValue.selectedFishTypes?.length ? formValue.selectedFishTypes.join(',') : null,
      start: formValue.dateRange.start || null,
      end: formValue.dateRange.end || null,
      tMin: formValue.temperature.min,
      tMax: formValue.temperature.max,
      wMin: formValue.windSpeed.min,
      wMax: formValue.windSpeed.max,
      wDir: formValue.windDirection || null,
      tides: formValue.tide?.length ? formValue.tide.join(',') : null,
      trip: formValue.trip_id || null, // handle string since ngValue is bound to trip_id instead of object now
      conds: formValue.conditions?.length ? formValue.conditions.join(',') : null,
      notes: formValue.anglerNotes || null
    };

    // Clean exact matches to defaults if desired, or just nulls
    Object.keys(params).forEach(k => {
      if (params[k] === null || params[k] === '') {
        params[k] = null; // sets to null to remove from queryParams if using queryParamsHandling: 'merge'
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl: true // don't push tons of history state for filters
    });
  }

  private emitFilter(formValue: any): void {
    const query: FilterQuery = { query: [] };

    if (formValue.selectedFishTypes?.length) {
      query.query.push({ fish_type: formValue.selectedFishTypes });
    }
    if (formValue.dateRange.start || formValue.dateRange.end) {
      query.query.push({
        date: {
          gte: formValue.dateRange.start ? new Date(formValue.dateRange.start).toISOString() : new Date(0).toISOString(),
          lte: formValue.dateRange.end ? new Date(formValue.dateRange.end).toISOString() : new Date().toISOString()
        }
      });
    }
    if (formValue.temperature.min || formValue.temperature.max) {
      query.query.push({
        temperature: {
          gte: Number(formValue.temperature.min) || 0,
          lte: Number(formValue.temperature.max) || 120
        }
      });
    }
    if (formValue.windSpeed.min || formValue.windSpeed.max) {
      query.query.push({
        wind_speed: {
          gte: Number(formValue.windSpeed.min) || 0,
          lte: Number(formValue.windSpeed.max) || 100
        }
      });
    }
    if (formValue.windDirection) {
      query.query.push({ wind_direction: formValue.windDirection });
    }
    if (formValue.tide?.length) {
      query.query.push({ tide: formValue.tide });
    }
    if (formValue.trip_id) {
      query.query.push({ trip_id: formValue.trip_id });
    }
    if (formValue.conditions?.length) {
      query.query.push({ conditions: formValue.conditions });
    }

    this.filterChange.emit({
      query,
      clientFilters: { notes: formValue.anglerNotes || '' }
    });
  }

  toggleHeatMap(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.heatMap = checked;
    this.heatmapToggleChange.emit(checked);
  }

  updateFishTypes(fishName: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current: string[] = this.filterForm.get('selectedFishTypes')?.value || [];
    const selected = checked ? [...current, fishName] : current.filter(f => f !== fishName);
    this.filterForm.patchValue({ selectedFishTypes: selected });
  }

  updateTides(tideState: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current: string[] = this.filterForm.get('tide')?.value || [];
    const selected = checked ? [...current, tideState] : current.filter(t => t !== tideState);
    this.filterForm.patchValue({ tide: selected });
  }

  updateConditions(condition: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current: string[] = this.filterForm.get('conditions')?.value || [];
    const selected = checked ? [...current, condition] : current.filter(c => c !== condition);
    this.filterForm.patchValue({ conditions: selected });
  }
}
